import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import '../../../styles/mpProducts.scss';
import { useSessionContext } from '../../../Context/SessionContext';
import AgeVerification from '../../../Components/AgeVerification';
import { tryFetch } from '../../../Helpers/FetchAPI';
import CustomDropdown from '../../../Components/Dropdown';
import useSale from './useSale';

interface Merchant {
  id: string;
  merchant_name: string;
  description: string;
  category: string;
  hero_url: string;
  logo_url?: string;
  status?: string;
  featured?: { title: string; mime_type: string; url: string; source: string }[] | null; 
  days_open?: string | null; 
  hours_open?: string | null; 
  hours_close?: string | null;
  merchant_slug: string; 
  city: string;
}

interface Product {
  id: string;
  store_id: string;
  product_id: string;
  product_name: string;
  product_type: string;
  product_slug: string;
  product_description: string;
  unit_price: string;
  media: { media_url: string }[];
  legal_age: boolean;
  is_sale: boolean;
  sale_price: string;
  sale_price_with_admin_fee: string;
}

interface ProductsListProps {
  products: Product[];
  merchant: Merchant; 
  wishlistProducts: string[];
  handleToggleLike: (productId: string) => void;
}

const ProductsList: React.FC<ProductsListProps> = ({ products, wishlistProducts, handleToggleLike, merchant }) => {
  const currency = process.env.REACT_APP_CURRENCY;
  const { session } = useSessionContext();
  const navigate = useNavigate();
  const [showSeeAll, setShowSeeAll] = useState(false);
  const [isGridView, setIsGridView] = useState(false);
  const productContainerRef = useRef<HTMLDivElement>(null);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});

  const [categoryView, setCategoryView] = useState<{ [key: string]: boolean }>({});
  const [categoryOverflow, setCategoryOverflow] = useState<{ [key: string]: boolean }>({});

  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [selectedProductSlug, setSelectedProductSlug] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [saleGridView, setSaleGridView] = useState(false);
  const [showSaleSeeAll, setShowSaleSeeAll] = useState(false);
  const { saleProducts: rawSaleProducts } = useSale();

  // const saleProducts = rawSaleProducts.map(product => ({
  //   ...product,
  //   product_id: product.product_id || product.id
  // }));

  const saleProducts: Product[] = rawSaleProducts.map((product) => ({
  ...product,
  product_id: product.product_id || product.id,
  sale_price_with_admin_fee:
    product.sale_price_with_admin_fee ||
    product.sale_price ||
    "0",
}));

  const merchantSaleProducts = saleProducts.filter(
  (product) => product.store_id === merchant.id
);


  const toggleGottSulitView = () => {
  // navigate(`/s/${merchant.merchant_slug}/GottSulit`);
  setSaleGridView((prevState) => !prevState);
  setShowSaleSeeAll(true);
  };

  const toggleView = () => {
    // navigate(`/s/${merchant.merchant_slug}/products`);
    setIsGridView((prevState) => !prevState);
    setShowSeeAll(true);
  };

  const toggleCategoryView = (productType: string) => {
    setCategoryView((prev) => ({
      ...prev,
      [productType]: !prev[productType],
    }));
  };

  const handleImageLoad = (imageKey: string) => {
    setLoadedImages((prevState) => ({
      ...prevState,
      [imageKey]: true,
    }));
  };

  const checkOverflow = () => {
    if (productContainerRef.current) {
      const { scrollWidth, clientWidth } = productContainerRef.current;
      setShowSeeAll(scrollWidth > clientWidth || isGridView);
    }
  };

   const checkSaleOverflow = () => {
    if (productContainerRef.current) {
      const { scrollWidth, clientWidth } = productContainerRef.current;
      setShowSaleSeeAll(scrollWidth > clientWidth || isGridView);
    }
  };

   const checkSaleCategoryOverflow = () => {
    Object.keys(groupedProducts).forEach((productType) => {
      const container = document.getElementById(`category-${productType}`);
      if (container) {
        const { scrollWidth, clientWidth } = container;
        setCategoryOverflow((prev) => ({
          ...prev,
          [productType]: scrollWidth > clientWidth || categoryView[productType],
        }));
      }
    });
  };

  const checkCategoryOverflow = () => {
    Object.keys(groupedProducts).forEach((productType) => {
      const container = document.getElementById(`category-${productType}`);
      if (container) {
        const { scrollWidth, clientWidth } = container;
        setCategoryOverflow((prev) => ({
          ...prev,
          [productType]: scrollWidth > clientWidth || categoryView[productType],
        }));
      }
    });
  };
    useEffect(() => {
        checkOverflow();
        checkCategoryOverflow();

        const handleResize = () => {
        checkOverflow();
        checkCategoryOverflow();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [products, isGridView, categoryView]);

     useEffect(() => {
        checkSaleOverflow();
        checkSaleCategoryOverflow();

        const handleResize = () => {
        checkSaleOverflow();
        checkSaleCategoryOverflow();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [products, isGridView, categoryView]);

    useEffect(() => {
        const timer = setTimeout(() => {
          checkCategoryOverflow();
        }, 300);
        return () => clearTimeout(timer);
      }, [products]);
      

    const handleHeartClick = (productId: string) => {
        if (!session.isAuthenticated) {
        navigate('/sec/log-in'); 
        } else {
        handleToggleLike(productId); 
        }
    };

    const handleProductClick = (product: Product) => {
      if (product.legal_age) {
        setSelectedProductSlug(product.product_slug);
        setShowAgeVerification(true);
      } else {
        navigate(`/merchant/${merchant.merchant_slug}/product/${product.product_slug}`);
      }
    };

    const handleAgeConfirm = () => {
      if (selectedProductSlug) {
        navigate(`/merchant/${merchant.merchant_slug}/product/${selectedProductSlug}`);
      }
      setShowAgeVerification(false);
      setSelectedProductSlug(null);
    };
    
    const handleAgeReject = () => {
      setShowAgeVerification(false);
      setSelectedProductSlug(null);
    };

  const groupedProducts = products.reduce((acc: { [key: string]: Product[] }, product) => {
    const { product_type } = product;
    if (!acc[product_type]) {
      acc[product_type] = [];
    }
    acc[product_type].push(product);
    return acc;
  }, {});

const uniqueCategories = Array.from(new Set(products.map(p => p.product_type)));
// const categoryOptions = [...uniqueCategories, 'All'].map(cat => ({
//   label: cat,
//   value: cat,
// }));
const categoryOptions = [
  { label: 'GottSulit', value: 'GottSulit' },
  ...Array.from(new Set(products.map(p => p.product_type))).map(cat => ({
    label: cat,
    value: cat,
  })),
  { label: 'All', value: 'All' }
];

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchQuery) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await tryFetch({
          url: `/search/${merchant.id}`,
          method: "GET",
          query: `?searchTerm=${encodeURIComponent(searchQuery)}&page=1&limit=10`,
        }); 

        if (response.is_success && response.data) {
          setResults(Array.isArray(response.data) ? response.data : []);
        } else {
          setResults([]);
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setError("Failed to load search results. Please try again.");
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchQuery, page, merchant.id]);

  // const activeProductList = searchQuery ? results : products;
  // const filteredProducts = activeProductList.filter((product) =>
  //   (selectedCategory === 'All' || product.product_type === selectedCategory)  &&
  //   product.product_name.toLowerCase().includes(searchQuery.toLowerCase())
  // );
   let activeProductList: Product[] = [];

if (searchQuery !== '') {
  activeProductList = results;
} else if (selectedCategory === 'GottSulit') {
  activeProductList = merchantSaleProducts;
} else if (selectedCategory === 'All') {
  const saleIds = new Set(merchantSaleProducts.map(p => p.product_id));
  const nonSaleProducts = products.filter(p => !saleIds.has(p.product_id));
  activeProductList = [...merchantSaleProducts, ...nonSaleProducts];
} else {
  activeProductList = products.filter(p => p.product_type === selectedCategory);
}

const filteredProducts = activeProductList.filter((product) =>
  product.product_name.toLowerCase().includes(searchQuery.toLowerCase())
);

  const groupedFilteredProducts = filteredProducts.reduce((acc: { [key: string]: Product[] }, product) => {
  const { product_type } = product;
  if (!acc[product_type]) {
    acc[product_type] = [];
  }
  acc[product_type].push(product);
  return acc;
}, {});

 const isInactive = merchant.status === 'INACTIVE';


  return (
    <div className="products-section">
      <div className="filter-bar">
        <CustomDropdown
          options={categoryOptions}
          selected={selectedCategory}
          onSelect={(val) => setSelectedCategory(val)}
        />
        <input
          type="text"
          placeholder={`Search ${merchant.merchant_name}'s product...`}
          className="product-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
        {(selectedCategory === 'All' || selectedCategory === 'GottSulit') && merchantSaleProducts.length > 0 && (
  <div className="c-products-section" id="gott-sulit-sale">
    <div className="products-header">
      <h2>GottSulit</h2>
      {showSaleSeeAll && (
              <button className="see-all" onClick={toggleGottSulitView}>
                {saleGridView ? 'Back to Slider' : 'See All'}
              </button>
            )}
    </div>
    <div className={saleGridView? 'products-grid' : 'products-slider'}>
      {merchantSaleProducts.map((product) => (
        <div
          key={product.product_id}
          className={`product-card ${isInactive ? 'inactive-merchant' : ''}`}
        >
          <div className="product-image-wrapper">
            <div className="product-image" onClick={() => handleProductClick(product)} style={{ cursor: 'pointer' }}>
              <img
                src={product.media[0]?.media_url}
                alt={product.product_name}
                width="169"
                height="169"
                className={loadedImages[`sale-${product.product_id}`] ? 'loaded' : ''}
                onLoad={() => handleImageLoad(`sale-${product.product_id}`)}
              />
            </div>
            {!isInactive && (
              <button className="heart-icon" onClick={() => handleHeartClick(product.product_id)}>
                {wishlistProducts.includes(product.product_id) ? (
                  <FaHeart color="#832b99" size="22" />
                ) : (
                  <FaRegHeart size="22" />
                )}
              </button>
            )}
          </div>
          <p className="product-name">{product.product_name}</p>
          <div className="product-price">
            <span className="sale-price">
              {currency} {parseFloat(product.sale_price_with_admin_fee).toFixed(2)}
            </span>
            <span className="old-price">
              {currency} {parseFloat(product.unit_price).toFixed(2)}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

 {(searchQuery || selectedCategory !== 'All') ? (
        Object.entries(groupedFilteredProducts).length > 0 ? (
          Object.entries(groupedFilteredProducts).map(([productType, productList]) => (
            <div key={productType} className="c-products-section">
              <div className="products-header">
                <h2>{productType}</h2>
              </div>
              <div className="products-grid">
                {productList.map((product) => (
                  <div
                    key={product.product_id}
                    className={`product-card ${isInactive ? 'inactive-merchant' : ''}`}
                  >
                    <div className="product-image-wrapper">
                      <div className="product-image">
                        {isInactive ? (
                          <div className="disabled-link">
                            <img
                              src={product.media[0]?.media_url}
                              alt={product.product_name}
                              width="169"
                              height="169"
                              className={loadedImages[`product-${product.product_id}`] ? 'loaded' : ''}
                              onLoad={() => handleImageLoad(`product-${product.product_id}`)}
                            />
                            <div className="offline-overlay"></div>
                          </div>
                        ) : (
                          <div onClick={() => handleProductClick(product)} style={{ cursor: 'pointer' }}>
                            <img
                              src={product.media[0]?.media_url}
                              alt={product.product_name}
                              width="169"
                              height="169"
                              className={loadedImages[`product-${product.product_id}`] ? 'loaded' : ''}
                              onLoad={() => handleImageLoad(`product-${product.product_id}`)}
                            />
                          </div>
                        )}
                        {!isInactive && (
                          <button className="heart-icon" onClick={() => handleHeartClick(product.id)}>
                            {wishlistProducts.includes(product.id) ? (
                              <FaHeart color="#832b99" size="22" />
                            ) : (
                              <FaRegHeart size="22" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="product-name">{product.product_name}</p>
                    <div className="product-price">
                    {product.is_sale ? (
                      <>
                        <span className="sale-price">
                          {currency} {parseFloat(product.sale_price_with_admin_fee).toFixed(2)}
                        </span>
                        <span className="old-price">
                          {currency} {parseFloat(product.unit_price).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="regular-price">
                        {currency} {parseFloat(product.unit_price).toFixed(2)}
                      </span>
                    )}
                  </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="fb-note">
            <p>No products found matching your search or category.</p>
          </div>
        )
      ) : (
        <>
          <div className="products-header">
            <h2>All Products</h2>
            {showSeeAll && (
              <button className="see-all" onClick={toggleView}>
                {isGridView ? 'Back to Slider' : 'See All'}
              </button>
            )}
          </div>
          <div className={isGridView ? 'products-grid' : 'products-slider'} ref={productContainerRef}>
            {products.length > 0 ? (
              products.map((product) => (
                <div
                  key={product.product_id}
                  className={`product-card ${isInactive ? 'inactive-merchant' : ''}`}
                >
                  <div className="product-image-wrapper">
                    <div className="product-image">
                      {isInactive ? (
                        <div className="disabled-link">
                          <img
                            src={product.media[0]?.media_url}
                            alt={product.product_name}
                            width="169"
                            height="169"
                            className={loadedImages[`product-${product.product_id}`] ? 'loaded' : ''}
                            onLoad={() => handleImageLoad(`product-${product.product_id}`)}
                          />
                          <div className="offline-overlay"></div>
                        </div>
                      ) : (
                        <div onClick={() => handleProductClick(product)} style={{ cursor: 'pointer' }}>
                          <img
                            src={product.media[0]?.media_url}
                            alt={product.product_name}
                            width="169"
                            height="169"
                            className={loadedImages[`product-${product.product_id}`] ? 'loaded' : ''}
                            onLoad={() => handleImageLoad(`product-${product.product_id}`)}
                          />
                        </div>
                      )}
                      {!isInactive && (
                        <button className="heart-icon" onClick={() => handleHeartClick(product.id)}>
                          {wishlistProducts.includes(product.id) ? <FaHeart color="#832b99" size="22" /> : <FaRegHeart size="22" />}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="product-name">{product.product_name}</p>
                  {/* <p className="product-price">{currency} {parseFloat(product.unit_price).toFixed(2)}</p> */}
                  <div className="product-price">
                    {product.is_sale ? (
                      <>
                        <span className="sale-price">
                          {currency} {parseFloat(product.sale_price_with_admin_fee).toFixed(2)}
                        </span>
                        <span className="old-price">
                          {currency} {parseFloat(product.unit_price).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="regular-price">
                        {currency} {parseFloat(product.unit_price).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="fb-note">
                <p>No products available.</p>
              </div>
            )}
          </div>
{Object.entries(groupedProducts).map(([productType, productList]) => (
            <div key={productType} className="c-products-section" id={`category-${productType}`}>
              <div className="products-header">
                <h2>{productType}</h2>
              </div>
              <div className="products-grid">
                {productList.map((product) => (
                  <div
                    key={product.product_id}
                    className={`product-card ${isInactive ? 'inactive-merchant' : ''}`}
                  >
                    <div className="product-image-wrapper">
                      <div className="product-image">
                        {isInactive ? (
                          <div className="disabled-link">
                            <img
                              src={product.media[0]?.media_url}
                              alt={product.product_name}
                              width="169"
                              height="169"
                              className={loadedImages[`product-${product.product_id}`] ? 'loaded' : ''}
                              onLoad={() => handleImageLoad(`product-${product.product_id}`)}
                            />
                            <div className="offline-overlay"></div>
                          </div>
                        ) : (
                          <div onClick={() => handleProductClick(product)} style={{ cursor: 'pointer' }}>
                            <img
                              src={product.media[0]?.media_url}
                              alt={product.product_name}
                              width="169"
                              height="169"
                              className={loadedImages[`product-${product.product_id}`] ? 'loaded' : ''}
                              onLoad={() => handleImageLoad(`product-${product.product_id}`)}
                            />
                          </div>
                        )}
                      
                      {!isInactive && (
                        <button className="heart-icon" onClick={() => handleHeartClick(product.id)}>
                          {wishlistProducts.includes(product.id) ? <FaHeart color="#832b99" size="22" /> : <FaRegHeart size="22" />}
                        </button>
                      )}
                      </div>
                    </div>

                    <div>
                      <p
                        className="product-name"
                        onClick={() => handleProductClick(product)}
                      >
                        {product.product_name}
                      </p>
                      <div className="product-price">
                    {product.is_sale ? (
                      <>
                        <span className="sale-price">
                          {currency} {parseFloat(product.sale_price).toFixed(2)}
                        </span>
                        <span className="old-price">
                          {currency} {parseFloat(product.unit_price).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="regular-price">
                        {currency} {parseFloat(product.unit_price).toFixed(2)}
                      </span>
                    )}
                  </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {showAgeVerification && (
        <AgeVerification
          onConfirm={handleAgeConfirm}
          onReject={handleAgeReject}
        />
      )}
    </div>
  );
};

export default ProductsList;